import useIsMobile from "#/src/hooks/useIsMobile";
import { useLogBrowserBlocked } from "@web-monorepo/hooks";
import FixBadPaths from "@web-monorepo/shared/router/FixBadPaths";
import { RouterNavigateProvider } from "@web-monorepo/shared/router/reactRouterNavigate";
import { ReactQueryDevTools } from "@web-monorepo/fetchers";

import "core-js/features/object/from-entries";
import "@web-monorepo/polyfills";

// eslint-disable-next-line @web-monorepo/no-css-file-imports
import "./_app.css";

import { ErrorHandlerContext, AppTopLevelErrorBoundary } from "@web-monorepo/app-shell";
import { ErrorBannerContainer } from "@web-monorepo/molecules";
import { UserActionsHistory } from "@web-monorepo/telemetry/userActionsHistory";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { refreshIfOldVersion, scheduleRefresh } from "@web-monorepo/shared/utils/midnightRefresh";
import { Outlet, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import * as Telemetry from "@web-monorepo/telemetry";
import type { ProductArea } from "@web-monorepo/api-types/util/teamProductAreas";
// Static import is safe: circular dep (router -> _app -> router) resolves at runtime
// because useRouteSpans only accesses `routes` inside a React effect, after all modules load.
import { routes } from "#/src/router";
import EnsureSessionAndLoggerContext from "#/src/pages/_components/EnsureSessionandLoggerContext";
import queryClient from "#/src/queryClient";
import { createWebErrorHandler, ignorableMarketplaceErrors } from "#/src/errorHandling";
import { dropHandledBareFetchErrors } from "#/src/utils/handledBareFetchErrors";
import { DDSProvider } from "@web-monorepo/dds";
import HelperWidgetProvider from "#/src/pages/_components/HelperWidgetProvider";
import { DevToolsSettings } from "#/src/utils/devTools";
import { QueryClientProvider } from "@tanstack/react-query";
import { setAppFeatureSwitchesConfig } from "#/src/hooks/useFeatureSwitches";
import { OTCPageProvider } from "@web-monorepo/one-time-codes";
import { TutorRealtimeProvider } from "#/src/providers/TutorRealtimeProvider";
import { isProduction } from "#/src/utils/env";
import { BodyClassFeatureSwitch } from "@web-monorepo/feature-switches/css-feature-switch";
import { localStorage } from "@web-monorepo/safe-browser-storage";
import ToastRenderingContainer from "#/src/pages/_components/ToastRenderingContainer";
import { MarketplaceTutorThemeProvider } from "#/src/hooks/useMarketplaceTutorTheme";
import { getPreReactHashRouterRedirectState } from "#/src/utils/hashRouterPathRedirect";
import { ParentReferralClaimProvider } from "#/src/pages/referrals/_components/ParentReferralClaimProvider";

const DevTools = lazy(() => import("#/src/utils/devTools").then((m) => ({ default: m.DevTools })));

const matchRoutesForSpans = (loc: { pathname: string }) => matchRoutes(routes, loc);

scheduleRefresh("marketplace");
refreshIfOldVersion({ buildAgeInDays: 3, releaseAgeInDays: 1 });

setAppFeatureSwitchesConfig();

declare global {
  interface Window {
    appVersion: string;
    buildNumber: string;
  }
}

// Computed at module load (before React) to prevent Safari "Load failed" errors.
// Query params → hash redirect must complete before any fetch requests start.
const preReactRedirect = (() => {
  const { search } = window.location;
  const redirectState = getPreReactHashRouterRedirectState(window.location);
  const { redirectUrl } = redirectState;

  if (!redirectUrl) return redirectState;

  // Preserve utm_source for iOS detection (uses @web-monorepo/safe-browser-storage)
  const params = new URLSearchParams(search);
  const utmSource = params.get("utm_source");
  if (utmSource) {
    localStorage.setItem("utmSource", utmSource);
  }

  window.location.replace(redirectUrl);
  return redirectState;
})();

if (isProduction) {
  Telemetry.init({
    dsn: "https://4755818d459c24619e89b402acc95172@sentry.classdojo.com/11",
    // Two independent paths to the same build-time APP_VERSION: the index.html `%APP_VERSION%`
    // substitution and the `Config` build define. Without a release Sentry silently discards
    // every release-health session (WEBP-523).
    release: Telemetry.resolveRelease(window.appVersion, Config.appVersion),
    // Sentry ownership routes events by the productArea tag (ai/rules/product-area.md), and
    // marketplace never sets one per route, so without this default its errors carry no owner.
    // As a scope tag it also overrides a productArea attached to an individual exception.
    initialScope: { tags: { productArea: "marketplace" satisfies ProductArea } },
    // Drops expected offline NoNetworkErrors, and BareFetchErrors whose callers already handle
    // the failure. Other request failures still report.
    beforeSend: dropHandledBareFetchErrors,
    tracing: {
      reactRouter: {
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      },
    },
  });
}
const App = () => {
  Telemetry.useRouteSpans({
    useLocation,
    matchRoutes: matchRoutesForSpans,
  });

  const userActionsHistory = new UserActionsHistory();
  if (typeof document !== "undefined") {
    userActionsHistory.listenToBrowserUrlChanges();
  }
  const [errorHandler] = useState(() => createWebErrorHandler(userActionsHistory));

  // Log analytics event if browser is blocked
  useLogBrowserBlocked();

  const { isDevToolsEnabled } = DevToolsSettings.useIsDevToolsEnabled();
  const { reactQueryDevToolsEnabled } = DevToolsSettings.useReactQueryDevToolsEnabled();
  const isMobile = useIsMobile();

  useLayoutEffect(() => {
    const mobileClassName = "isMobile";
    const desktopClassName = "isDesktop";
    if (isMobile) {
      document.body.classList.add(mobileClassName);
      document.body.classList.remove(desktopClassName);
    } else {
      document.body.classList.add(desktopClassName);
      document.body.classList.remove(mobileClassName);
    }
  }, [isMobile]);

  // Never mount providers or start requests while the pre-React redirect is in progress.
  if (preReactRedirect.isRedirecting) {
    return null;
  }

  return (
    <ErrorHandlerContext.Provider value={errorHandler}>
      <DDSProvider>
        <ErrorBannerContainer />
        <AppTopLevelErrorBoundary errorHandler={errorHandler} excludeErrors={ignorableMarketplaceErrors}>
          <QueryClientProvider client={queryClient}>
            {isDevToolsEnabled && reactQueryDevToolsEnabled && <ReactQueryDevTools />}
            <EnsureSessionAndLoggerContext>
              <MarketplaceTutorThemeProvider>
                <BodyClassFeatureSwitch
                  switchName={"web_marketplace_normalize_typography_padding_margin"}
                  bodyClassName="normalizeTypographyPaddingMargin"
                />
                <TutorRealtimeProvider>
                  <HelperWidgetProvider>
                    <RouterNavigateProvider />
                    <FixBadPaths />
                    <ToastRenderingContainer>
                      {/* Note - OTC only exists for dojo parents at time of implementation, just for dojo parents logging into marketplace */}
                      <OTCPageProvider entityType="parent">
                        <ParentReferralClaimProvider>
                          <Outlet />
                        </ParentReferralClaimProvider>
                      </OTCPageProvider>
                      {isDevToolsEnabled && (
                        <Suspense fallback={null}>
                          <DevTools />
                        </Suspense>
                      )}
                    </ToastRenderingContainer>
                  </HelperWidgetProvider>
                </TutorRealtimeProvider>
              </MarketplaceTutorThemeProvider>
            </EnsureSessionAndLoggerContext>
          </QueryClientProvider>
        </AppTopLevelErrorBoundary>
      </DDSProvider>
    </ErrorHandlerContext.Provider>
  );
};

export default App;
